import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sprintf } from 'sprintf';

import { InlinedUser } from 'components/users/InlinedUser';
import { timeoutPromise } from 'utils/helpers';
import { SpinnerWithLabel } from 'components/Spinner';

import { requestGiftCodeDiscountClaim } from 'features/giftCodeDiscounts/api';

type Props = {
  code: string;
  onBack: () => void;
  onClaimed: () => void;
};

const formatCode = (code: string) => {
  if (code.length === 8) {
    return `${code.substring(0, 4)} ${code.substring(4)}`;
  } else {
    return code;
  }
};

export const ConfirmSection: React.FC<Props> = (props) => {
  const [error, setError] = React.useState<string | undefined>();
  const [claiming, setClaiming] = React.useState(false);

  return (
    <div className='text-center px-1'>
      <h1 className='standfirst standfirst--lead lh-tight mb-1 mb-sm-2'>Review and confirm</h1>
      <table className='table table--semi-bordered mb-2'>
        <tbody>
          <tr>
            <td className='py-1 py-sm-2'>
              <span className='key-value__value lh-1'>Voucher code</span>
            </td>
            <td className='py-1 py-sm-2 vam'>
              <span className='key-value__value fs-16'>{formatCode(props.code)}</span>
            </td>
          </tr>
          <tr>
            <td className='py-1 py-sm-2' style={{ borderColor: '#3C3C3D' }}>
              <span className='key-value__value lh-1' style={{ paddingTop: 1 }}>
                Art stream duration
              </span>
            </td>
            <td className='py-1 py-sm-2 vam' style={{ borderColor: '#3C3C3D' }}>
              <span className='key-value__value fs-16'>6 months</span>
            </td>
          </tr>
          <tr>
            <td className='py-1 py-sm-2' style={{ borderColor: '#3C3C3D' }}>
              <span className='key-value__value lh-1'>User logged in</span>
            </td>
            <td className='py-1 py-sm-2 text-light vam' style={{ borderColor: '#3C3C3D' }}>
              {sedition.loggedInUser ? (
                <InlinedUser
                  user={sedition.loggedInUser}
                  className='inlined-user--large inlined-user--no-decoration'
                  target='_blank'
                  style={{ marginTop: '-7px' }}
                  linkStyle={{ border: '0 none', padding: '2px 0 0 0' }}
                />
              ) : null}
            </td>
          </tr>
        </tbody>
      </table>
      <AnimatePresence exitBeforeEnter>
        <div style={{ minHeight: 80 }}>
          {!claiming ? (
            <>
              {error ? (
                <div
                  style={{ minHeight: 43 }}
                  dangerouslySetInnerHTML={{
                    __html: error,
                  }}
                />
              ) : (
                <motion.button
                  key='cta'
                  onClick={() => {
                    setClaiming(true);
                    Promise.all([requestGiftCodeDiscountClaim(props.code), timeoutPromise(2000)])
                      .then(props.onClaimed)
                      .catch(({ response }) => {
                        setError(
                          response.status === 500
                            ? sprintf(
                                'Unable to claim gift code. Please contact support at %s',
                                '<a class="link-border" href="mailto:support@seditionart.com">support@seditionart.com</a>',
                              )
                            : response.data.error,
                        );
                        setClaiming(false);
                      });
                  }}
                  className='btn btn--cta'
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                >
                  Activate Art Stream
                </motion.button>
              )}
              <div className='mb-1' />
              <a
                href='#'
                onClick={(event) => {
                  event.preventDefault();
                  props.onBack();
                }}
                className='link-border link-border--back'
              >
                <span className='link-border'>start over</span>
              </a>
            </>
          ) : null}
          {claiming ? (
            <motion.div
              key='loading'
              style={{ margin: '0 auto', display: 'inline-block' }}
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
            >
              <SpinnerWithLabel size='s' light>
                Activating...
              </SpinnerWithLabel>
            </motion.div>
          ) : null}
        </div>
      </AnimatePresence>
    </div>
  );
};
